/**
 * Advance the read cursor once the thread has actually been looked at.
 *
 * The delay is the whole point: a user tabbing past a thread should not clear its badge.
 * The legacy surfaces disagreed on this — the member portal auto-cleared after 5s while
 * the office thread required a manual "Mark as read" click — and the two behaviours were
 * two different mutations against two different tables.
 *
 * Safe to re-fire: `POST /:cid/read` never moves the cursor backwards, so a re-render,
 * a second tab, or a retry cannot re-light a badge.
 */

import { useEffect, useRef } from "react";

import type { ReadOnViewTarget } from "../_libs/read-on-view-target";

export type UseMarkReadOnViewOptions = {
  enabled: boolean;
  /** What to read once the delay passes — `computeReadOnViewTarget` decides. */
  target: ReadOnViewTarget;
  delayMs: number;
  /** No id reads the whole thread. */
  markRead: (messageId?: string) => Promise<unknown>;
};

export function useMarkReadOnView({
  enabled,
  target,
  delayMs,
  markRead,
}: UseMarkReadOnViewOptions): void {
  // The mutation identity changes every render; holding it in a ref keeps the timer
  // keyed on the target alone, so a re-render does not restart the countdown.
  const markReadRef = useRef(markRead);
  markReadRef.current = markRead;

  // Primitives, because the target is a fresh object every render.
  const targetKind = target.kind;
  const upToMessageId = target.kind === "upTo" ? target.messageId : undefined;

  useEffect(() => {
    if (!enabled || targetKind === "none") return;

    const timer = setTimeout(() => {
      markReadRef.current(upToMessageId).catch((error: unknown) => {
        // A failed read-cursor advance is not worth a toast — the badge simply stays
        // lit and the next view retries.
        console.error("Failed to mark channel read:", error);
      });
    }, delayMs);

    return () => clearTimeout(timer);
  }, [enabled, targetKind, upToMessageId, delayMs]);
}
